import TEALIUM from "../../enums/tealium-enums";
import { track } from "../../util/tealium";
import type { SaqTab } from "../../../components/ActionReports/SaqActionReport/SaqActionReport";

/**
 * Tracks the SAQ Action Report page view on load.
 * page_section: SAQ, page_category: action_report, page_detail: list_view_{YYYYMM}
 */
export const saqPageViewTrack = (period: string) => {
  track(
    TEALIUM.U_TAG_METHOD.VIEW,
    "",
    TEALIUM.CATEGORY.SAQ_AR,
    TEALIUM.CATEGORY.ACTION_REPORT,
    `list_view_${period}`,
    "",
  );
};

const TAB_CLICK_ACTIONS: Record<SaqTab, string> = {
  upForRenewal: "SAQ_renewal",
  incomplete: "SAQ_incomplete",
  completed: "SAQ_completed",
};

/**
 * Tracks tab click on the SAQ Action Report.
 * click_action: SAQ_renewal | SAQ_incomplete | SAQ_completed
 * click_category: action_report, click_detail: SAQ
 */
export const saqTabClickTrack = (tab: SaqTab) => {
  track(
    TEALIUM.U_TAG_METHOD.LINK,
    TEALIUM.EVENT_NAME.CLICK_ACTION,
    "",
    TEALIUM.CATEGORY.ACTION_REPORT,
    TEALIUM.CATEGORY.SAQ_AR,
    TAB_CLICK_ACTIONS[tab],
  );
};

/**
 * Tracks export button click.
 * click_action: export, click_category: action_report, click_detail: SAQ
 */
export const saqExportTrack = () => {
  track(
    TEALIUM.U_TAG_METHOD.LINK,
    TEALIUM.EVENT_NAME.CLICK_ACTION,
    "",
    TEALIUM.CATEGORY.ACTION_REPORT,
    TEALIUM.CATEGORY.SAQ_AR,
    "export",
  );
};

/**
 * Tracks click on the customize KPI button.
 * click_action: customize_KPI, click_category: action_report, click_detail: SAQ
 */
export const saqCustomizeKpiTrack = () => {
  track(
    TEALIUM.U_TAG_METHOD.LINK,
    TEALIUM.EVENT_NAME.CLICK_ACTION,
    "",
    TEALIUM.CATEGORY.ACTION_REPORT,
    TEALIUM.CATEGORY.SAQ_AR,
    "customize_KPI",
  );
};

/**
 * Tracks cancel action in the KPI selector modal.
 * click_action: cancel_customize_KPI, click_category: action_report, click_detail: SAQ
 */
export const saqCancelCustomizeKpiTrack = () => {
  track(
    TEALIUM.U_TAG_METHOD.LINK,
    TEALIUM.EVENT_NAME.CLICK_ACTION,
    "",
    TEALIUM.CATEGORY.ACTION_REPORT,
    TEALIUM.CATEGORY.SAQ_AR,
    "cancel_customize_KPI",
  );
};

/**
 * Tracks apply action in the KPI selector modal.
 * click_action: apply_customize_KPI, click_category: action_report, click_detail: SAQ
 */
export const saqApplyCustomizeKpiTrack = () => {
  track(
    TEALIUM.U_TAG_METHOD.LINK,
    TEALIUM.EVENT_NAME.CLICK_ACTION,
    "",
    TEALIUM.CATEGORY.ACTION_REPORT,
    TEALIUM.CATEGORY.SAQ_AR,
    "apply_customize_KPI",
  );
};

/**
 * Tracks ABO profile click.
 * click_action: profile, click_category: action_report, click_detail: SAQ
 */
export const saqProfileTrack = () => {
  track(
    TEALIUM.U_TAG_METHOD.LINK,
    TEALIUM.EVENT_NAME.CLICK_ACTION,
    "",
    TEALIUM.CATEGORY.ACTION_REPORT,
    TEALIUM.CATEGORY.SAQ_AR,
    "profile",
  );
};

/**
 * Tracks adding an ABO to the watchlist.
 * click_action: add_to_watchlist, click_category: action_report, click_detail: SAQ
 */
export const saqAddToWatchlistTrack = () => {
  track(
    TEALIUM.U_TAG_METHOD.LINK,
    TEALIUM.EVENT_NAME.CLICK_ACTION,
    "",
    TEALIUM.CATEGORY.ACTION_REPORT,
    TEALIUM.CATEGORY.SAQ_AR,
    "add_to_watchlist",
  );
};

/**
 * Tracks removing an ABO from the watchlist.
 * click_action: remove_from_watchlist, click_category: action_report, click_detail: SAQ
 */
export const saqRemoveFromWatchlistTrack = () => {
  track(
    TEALIUM.U_TAG_METHOD.LINK,
    TEALIUM.EVENT_NAME.CLICK_ACTION,
    "",
    TEALIUM.CATEGORY.ACTION_REPORT,
    TEALIUM.CATEGORY.SAQ_AR,
    "remove_from_watchlist",
  );
};

/**
 * Tracks phone (call) icon click.
 * click_action: call, click_category: action_report, click_detail: SAQ
 */
export const saqPhoneIconTrack = () => {
  track(
    TEALIUM.U_TAG_METHOD.LINK,
    TEALIUM.EVENT_NAME.CLICK_ACTION,
    "",
    TEALIUM.CATEGORY.ACTION_REPORT,
    TEALIUM.CATEGORY.SAQ_AR,
    "call",
  );
};

/**
 * Tracks message icon click.
 * click_action: message, click_category: action_report, click_detail: SAQ
 */
export const saqMessageIconTrack = () => {
  track(
    TEALIUM.U_TAG_METHOD.LINK,
    TEALIUM.EVENT_NAME.CLICK_ACTION,
    "",
    TEALIUM.CATEGORY.ACTION_REPORT,
    TEALIUM.CATEGORY.SAQ_AR,
    "message",
  );
};

/**
 * Tracks search focus/interaction.
 * click_action: search, click_category: action_report, click_detail: SAQ
 */
export const saqSearchTrack = () => {
  track(
    TEALIUM.U_TAG_METHOD.LINK,
    TEALIUM.EVENT_NAME.CLICK_ACTION,
    "",
    TEALIUM.CATEGORY.ACTION_REPORT,
    TEALIUM.CATEGORY.SAQ_AR,
    "search",
  );
};

/**
 * Tracks group leader filter selection.
 * click_action: select_group_leader, click_category: action_report, click_detail: SAQ
 */
export const saqSelectGroupLeaderTrack = () => {
  track(
    TEALIUM.U_TAG_METHOD.LINK,
    TEALIUM.EVENT_NAME.CLICK_ACTION,
    "",
    TEALIUM.CATEGORY.ACTION_REPORT,
    TEALIUM.CATEGORY.SAQ_AR,
    "select_group_leader",
  );
};
